import React, { useRef, useState } from 'react';
import { readString } from 'react-papaparse';
import {
  collection,
  addDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { useTranslation } from '../i18n';
import { analytics, db } from '../firebaseConfig';
import { generateBookKey } from '../utils/functions';
import closeIcon from '../images/close-line.svg';
import exampleData from '../example-book-data.csv';
import './UploadCSV.css';
import { logEvent } from 'firebase/analytics';

const UploadCSV = ({ user, books, fetchBooks, showNotification }) => {
  const { t } = useTranslation();
  const [csvFile, setCsvFile] = useState(null);
  const [csvFileName, setCsvFileName] = useState(null);
  const [dialogIsOpen, setDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [csvRows, setCSVRows] = useState(0);
  const [csvRowsParsed, setCSVRowsParsed] = useState(0);

  const dialogRef = useRef(null);
  const dropAreaRef = useRef(null);

  const openDialog = () => {
    if (dialogRef.current) {
      setDialogOpen(true);
      dialogRef.current.showModal();
    }
  };

  const closeDialog = () => {
    if (importing) {
      return false;
    }
    if (dialogRef.current) {
      setDialogOpen(false);
      dialogRef.current.close();
    }
    setCsvFile(null);
    setCsvFileName(null);
    setImporting(false);
    setCSVRows(0);
    setCSVRowsParsed(0);
  };

  const handleFileUpload = (file) => {
    setCsvFile(file);
    setCsvFileName(file.name);
  };

  const handleFileSelect = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.target.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleFileParseAndUpload = async () => {
    if (!csvFile) {
      console.error('Please select a CSV file to upload.');
      return;
    }
    setImporting(true);
    let booksParsed = 0;
    let booksUploaded = 0;

    const reader = new FileReader();
    reader.onload = async (e) => {
      const csvData = e.target.result;
      const parsedData = readString(csvData, { header: true }).data;
      setCSVRows(parsedData.length);

      try {
        for (const row of parsedData) {
          let isABook = false;
          let bookExists = false;

          if (row['Title']) {
            isABook = true;
          }

          // Safely parse the sessions column
          let parsedSessions = [];
          if (row['Sessions']) {
            try {
              const sessionsArray = JSON.parse(row['Sessions']);
              parsedSessions = sessionsArray.map((session) => {
                if (session.date && session.date.seconds !== undefined) {
                  // Convert to Firestore Timestamp
                  session.date = new Timestamp(
                    session.date.seconds,
                    session.date.nanoseconds,
                  );
                }
                return session;
              });
            } catch (error) {
              console.error('Error parsing sessions data: ', error);
              parsedSessions = []; // Fallback to an empty array if parsing fails
            }
          }

          const sanitizeString = (str) => {
            return !str ? false : str.replace(/[<>"';()&+%#]/g, ''); // Strip potentially harmful characters
          };

          const isValidDate = (dateStr) => {
            const date = new Date(dateStr);
            return !isNaN(date.getTime()); // Check if date is valid
          };

          let status;
          switch (sanitizeString(row['Status'] || row['Exclusive Shelf'])) {
            case 'reading':
              status = 'reading';
              break;
            case 'currently-reading':
              status = 'reading';
              break;
            case 'read':
              status = 'read';
              break;
            case 'dnf':
              status = 'dnf';
              break;
            case 'to-read':
              status = 'wantToRead';
              break;
            default:
              status = 'wantToRead';
              break;
          }

          const formattedData = isABook
            ? {
                title: sanitizeString(row['Title']) || '',
                author_name: sanitizeString(row['Author']) || '',
                number_of_pages: isNaN(
                  parseInt(row['Pages'] || row['Number of Pages']),
                )
                  ? null
                  : Math.abs(parseInt(row['Pages'])) ||
                    Math.abs(parseInt(row['Number of Pages'])),
                isbn: sanitizeString(row['ISBN']) || '',
                status: status,
                pages: isNaN(parseInt(row['Pages read']))
                  ? null
                  : Math.abs(parseInt(row['Pages read'])),
                time: isNaN(parseInt(row['Time read (sec.)']))
                  ? null
                  : Math.abs(parseInt(row['Time read (sec.)'])),
                cover_url: row['Cover URL']
                  ? [sanitizeString(row['Cover URL'])]
                  : [false],
                rating: sanitizeString(row['Rating'] || row['My Rating']) || '',
                review: sanitizeString(row['Review'] || row['My Review']) || '',
                starred: false,
                sessions: parsedSessions, // Assign the parsed sessions object
                dateStarted:
                  (row['Date Started'] && isValidDate(row['Date Started'])) ||
                  (row['Date Added'] && isValidDate(row['Date Added']))
                    ? isValidDate(row['Date Started'])
                      ? new Date(row['Date Started'])
                      : new Date(row['Date Added'])
                    : null,
                dateFinished:
                  (row['Date Finished'] && isValidDate(row['Date Finished'])) ||
                  (row['Date Read'] && isValidDate(row['Date Read']))
                    ? isValidDate(row['Date Finished'])
                      ? new Date(row['Date Finished'])
                      : new Date(row['Date Read'])
                    : null,
                publisher: sanitizeString(row['Publisher']) || '',
                publish_date:
                  sanitizeString(row['Published'] || row['Year Published']) ||
                  '',
                userID: sanitizeString(user.uid),
                bookID: generateBookKey('_import_'),
                createdAt: serverTimestamp(),
              }
            : {};

          if (isABook) {
            booksParsed++;
            bookExists = books.some(
              (book) => book.title === formattedData.title,
            );

            if (!bookExists) {
              booksUploaded++;
              // Add document to Firestore
              await addDoc(collection(db, 'UserBooks'), formattedData);
            }
          }

          // Update the progress bar
          setCSVRowsParsed((prevValue) => prevValue + 1);
        }
      } catch (error) {
        console.error('Error uploading CSV data: ', error);
      } finally {
        if (booksUploaded > 0) {
          fetchBooks();
          showNotification(
            `${booksUploaded} ${booksUploaded === 1 ? t.book : t.books} ${t.imported}.`,
          );
          logEvent(analytics, 'account_settings_upload_csv', {
            books_uploaded: booksUploaded,
          });
        } else {
          showNotification(
            booksParsed > 0
              ? `${booksParsed} ${booksParsed === 1 ? t.book : t.books} ${t.alreadyimported}.`
              : `${t.nothingimported}`,
          );
        }
        closeDialog();
      }
    };
    reader.readAsText(csvFile);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dropAreaRef.current.classList.add('dragging');
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dropAreaRef.current.classList.remove('dragging');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dropAreaRef.current.classList.remove('dragging');
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  return [
    <div
      onClick={openDialog}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          openDialog();
        }
      }}
      tabIndex={0}
      key="button"
    >
      {t.importtocsv}
    </div>,
    <dialog ref={dialogRef} key="dialog">
      {dialogIsOpen && (
        <div className="fullscreen-container" tabIndex={-1}>
          <button
            className={`fullscreen-close ${importing ? 'pt-65' : null}`}
            onClick={closeDialog}
            disabled={importing}
          >
            <img src={closeIcon} width={42} height={42} alt={t.close} />
            {t.close}
          </button>

          <div className="card-wrapper">
            <div className="card">
              <h3>{t.importhelpheader}</h3>
              <div className="pt-65">{t.importhelptext}</div>
              <div className="pt-65">{t.importsubhelp}</div>
              <div
                ref={dropAreaRef}
                className="drop-area"
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => document.getElementById('csv-input').click()}
              >
                <input
                  id="csv-input"
                  type="file"
                  accept=".csv"
                  onChange={(e) => handleFileSelect(e)}
                  style={{ display: 'none' }}
                  disabled={importing}
                />
                <p>{csvFile ? csvFileName : t.drophint}</p>
              </div>
              <button
                disabled={!csvFile || importing}
                className="btn btn-primary"
                onClick={handleFileParseAndUpload}
              >
                {t.importtocsv}
              </button>
              {/* Display progress */}
              {csvRows > 0 && (
                <div className="progress-wrapper">
                  <div className="progress-label">
                    <div className="pt-65 nowrap">{t.csvprogress}</div>
                    <div className="pt-65 nowrap">
                      {Math.round(
                        100 * ((csvRowsParsed ? csvRowsParsed : 0) / csvRows),
                      ).toLocaleString()}
                      {' %'}
                    </div>
                  </div>
                  <progress
                    className="progress-bar"
                    max={csvRows}
                    value={csvRowsParsed}
                  />
                </div>
              )}
            </div>
            <a
              className="link centered"
              href={exampleData}
              download="example-book-data"
            >
              {t.downloadexamplefile}
            </a>
          </div>

          <div className="btn-group-wrapper">
            <div className="btn-group">
              <button
                disabled={importing}
                className="btn btn-secondary w-100"
                onClick={() => closeDialog()}
              >
                {t.cancel}
              </button>
            </div>
          </div>
        </div>
      )}
    </dialog>,
  ];
};

export default UploadCSV;
